import type { SidebarPositions } from "$root/components/common/Sidebar-shared";
import { useDraggable } from "@mdotm/shadcn/legacy/headless";
import { useWindowSize } from "@mdotm/shadcn/legacy/react-extensions";
import { atomWithStorage, useAtom } from "@mdotm/shadcn/legacy/reactivity";
import type { Updater } from "@mdotm/shadcn/legacy/utils";
import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";

/**
 * Sphere AI chat panel layout. The chat can render two ways:
 * - **docked** (`position: relative`) — an in-flow flex sibling of the page content that
 *   reserves layout space, so the page reflows to the remaining width instead of being covered.
 * - **overlay** (`position: fixed`) — floats over the page, reserving no space (the historical
 *   behavior, kept for narrow viewports).
 *
 * Docking is gated to wide viewports exactly like the AppDrawer pin (see AppDrawer-shared.ts):
 * the user preference persists in storage, but is only *applied* while the window is wide enough —
 * shrink below the threshold and it falls back to overlay; grow back and the preference returns.
 */
export const chatDockWidthThreshold = 1400;

/** Fixed width of the floating overlay panel. */
export const OVERLAY_CHAT_WIDTH = 640;

/** Docked panel width — responsive so it grows on larger screens while leaving the page room. */
export const DOCKED_CHAT_WIDTH = "clamp(24rem, 28vw, 44rem)";

/** Narrowest a drag can make the panel — the docked default's own floor (24rem), below which the chat stops being usable. */
export const MIN_CHAT_WIDTH = 384;

/** How far one arrow-key press on the resize handle moves the edge. */
const CHAT_WIDTH_KEY_STEP = 32;

/**
 * Widest a drag can make the panel: half the viewport. Past that it stops reading as a side panel,
 * and "as wide as the screen" is already covered by the header's fullscreen toggle.
 */
export function maxChatWidth(windowWidth: number): number {
	return Math.round(windowWidth / 2);
}

/**
 * The width the panel may actually take, given the viewport. The cap wins over the floor: on a
 * viewport too narrow to fit {@link MIN_CHAT_WIDTH} in half, half is still all the panel may have.
 */
export function clampChatWidth(value: number, windowWidth: number): number {
	return Math.min(Math.max(Math.round(value), MIN_CHAT_WIDTH), maxChatWidth(windowWidth));
}

/**
 * The geometry of one moment of a resize drag: the width the panel would take, and where to put the
 * guide line standing in for the edge being dragged.
 *
 * The docked side decides both, and by the same sign: a left-docked panel's free edge is its right
 * one, so it widens as the pointer moves right and its edge lies that far to the right of the fixed
 * side. A right-docked panel mirrors both halves of that.
 */
export function chatResizeFrame({
	startWidth,
	startEdge,
	dx,
	position,
	windowWidth,
}: {
	/** The panel's width when the drag began. */
	startWidth: number;
	/** Viewport x of the panel's *fixed* edge — the one the drag leaves alone. */
	startEdge: number;
	/** How far the pointer has travelled since the drag began. */
	dx: number;
	position: SidebarPositions;
	windowWidth: number;
}): { width: number; guideX: number } {
	const direction = position === "left" ? 1 : -1;
	const width = clampChatWidth(startWidth + direction * dx, windowWidth);
	return { width, guideX: startEdge + direction * width };
}

/**
 * The width (px) the user dragged the panel to, or `null` while it's still at its mode default —
 * the responsive clamp when docked, {@link OVERLAY_CHAT_WIDTH} when floating.
 *
 * One preference for both modes rather than one each: a drag starts from the width that's on screen
 * (see {@link useChatPanelWidth}), so switching modes after a resize keeps the size the user chose
 * instead of snapping back to a per-mode number they never picked.
 */
export const chatPanelWidth$ = atomWithStorage<number | null>("sphereAIChatPanelWidth", null);

/**
 * The chat's layout mode — the three are mutually exclusive, so they live in one preference:
 * - `docked` — reserves layout space (only *applied* on wide viewports; see {@link useChatDockState}).
 * - `overlay` — floats over the page at a fixed width.
 * - `fullscreen` — floats over the page, stretched to fill it (minus the nav drawer).
 */
export type ChatLayoutMode = "docked" | "overlay" | "fullscreen";

/** Persisted chat layout mode. Survives resize/reload; `docked` is conditionally applied via {@link useChatDockState}. */
export const chatLayoutMode$ = atomWithStorage<ChatLayoutMode>("sphereAIChatLayoutMode", "docked");

export function useChatDockState(): {
	/** Whether `docked` is the persisted preference (regardless of viewport). */
	docked: boolean;
	/** Whether the viewport is wide enough to dock at all. */
	canDock: boolean;
	/** The effective state: docked only when the preference is set AND the viewport allows it. */
	isDocked: boolean;
	setDocked(v: boolean | Updater<boolean>): void;
	/** Whether the chat is stretched to fill the viewport. */
	fullscreen: boolean;
	/** Toggle fullscreen. Exiting falls back to a plain overlay (docking and fullscreen can't coexist). */
	toggleFullscreen(): void;
} {
	const [mode, setMode] = useAtom(chatLayoutMode$);
	const { width: windowWidth } = useWindowSize();
	const canDock = windowWidth > chatDockWidthThreshold;
	const docked = mode === "docked";
	const fullscreen = mode === "fullscreen";
	// Pin toggle maps to the docked/overlay pair; fullscreen is reached only via toggleFullscreen.
	const setDocked = useCallback(
		(v: boolean | Updater<boolean>) => {
			setMode((prev) => {
				const next = typeof v === "function" ? v(prev === "docked") : v;
				return next ? "docked" : "overlay";
			});
		},
		[setMode],
	);
	const toggleFullscreen = useCallback(() => {
		setMode((prev) => (prev === "fullscreen" ? "overlay" : "fullscreen"));
	}, [setMode]);
	return {
		docked,
		canDock,
		isDocked: canDock && docked,
		setDocked,
		fullscreen,
		toggleFullscreen,
	};
}

/** What the panel's resize grip needs to be wired up. */
export type UseChatPanelWidthParams = {
	/**
	 * The panel root. Measured at drag start, because for an untouched panel the current width exists
	 * only on screen: the docked default is a CSS clamp this module can't evaluate.
	 */
	panelRef: React.RefObject<HTMLElement | null>;
	/** The grip. In state rather than a ref so the drag listeners attach once it has mounted. */
	handleEl: HTMLElement | null;
	/** The guide line the drag moves in the panel's place — see {@link useChatPanelWidth}. */
	guideRef: React.RefObject<HTMLElement | null>;
	/** Which side the panel is docked to — it decides which way a drag widens it. */
	position: SidebarPositions;
};

/** The width to render the panel at, plus the handlers its resize grip needs. */
export type ChatPanelWidth = {
	/** The panel's width in px, or `null` to leave it at the mode default. */
	width: number | null;
	/** Whether a resize is in flight — the panel must not animate its width into place. */
	resizing: boolean;
	/** Resize by `delta` px (positive = wider), for arrow keys on the grip. */
	nudgeWidth(delta: number): void;
	/** Back to the mode default. */
	resetWidth(): void;
};

/**
 * Manual panel resize, driven by the grip on the panel's inner edge.
 *
 * **The drag does not resize the panel.** A docked panel is in the page's flex flow, so every width
 * it takes reflows the page beside it — and these pages are content-heavy enough that doing that per
 * mousemove drops frames. So the gesture moves a 2px guide line to where the edge *would* land, by
 * writing `transform` straight to the guide element (no React state, so nothing re-renders while the
 * pointer moves), and the panel takes its new width once, on release.
 *
 * The stored width is clamped on *read* rather than on write, exactly like the docked preference: a
 * width that no longer fits a shrunken viewport is applied narrower and restored in full when the
 * window grows back, instead of being permanently overwritten by a resize the user didn't ask for.
 */
export function useChatPanelWidth({ panelRef, handleEl, guideRef, position }: UseChatPanelWidthParams): ChatPanelWidth {
	const [stored, setStored] = useAtom(chatPanelWidth$);
	const { width: windowWidth } = useWindowSize();

	// The gesture's origin, captured on mousedown: the panel's width, and the viewport x of the edge the
	// drag leaves alone. Both hold for the whole drag precisely because the panel doesn't move during it.
	const startWidthRef = useRef(0);
	const startEdgeRef = useRef(0);
	// Where the last move put the guide. This, not a re-measure, is what gets committed on release.
	const nextWidthRef = useRef<number | null>(null);

	// Held true across the render that applies the committed width, so the panel lands at its new size
	// instead of easing there: a 300ms width animation on a docked panel is 300ms of reflowing the page
	// behind it, which is the cost this whole approach exists to avoid.
	const [committing, setCommitting] = useState(false);
	useLayoutEffect(() => {
		if (!committing) {
			return;
		}
		// Forces the browser to lay the committed width out *now*, while the transition is still off.
		// Without that flush, re-enabling it below can land in the same style recalculation as the width
		// change — and the browser would animate the change after all, which is what this is avoiding.
		void panelRef.current?.offsetWidth;
		setCommitting(false);
	}, [committing, panelRef]);

	const { dragging } = useDraggable({
		draggableEl: handleEl,
		onDragStart: () => {
			const panel = panelRef.current?.getBoundingClientRect();
			startWidthRef.current = panel?.width ?? MIN_CHAT_WIDTH;
			startEdgeRef.current = (position === "left" ? panel?.left : panel?.right) ?? 0;
			nextWidthRef.current = null;
		},
		onDragMove: (from, to) => {
			const { width, guideX } = chatResizeFrame({
				startWidth: startWidthRef.current,
				startEdge: startEdgeRef.current,
				dx: to.x - from.x,
				position,
				windowWidth,
			});
			nextWidthRef.current = width;
			const guide = guideRef.current;
			if (guide) {
				// Written imperatively on purpose: this runs per mousemove, and routing it through state
				// would re-render the panel — chat thread included — on every frame of the drag.
				guide.style.visibility = "visible";
				guide.style.transform = `translateX(${guideX}px)`;
			}
		},
		onDragEnd: (from, to) => {
			if (guideRef.current) {
				guideRef.current.style.visibility = "hidden";
			}
			// A press that never moved is not a resize — committing there would quietly pin the panel to
			// its current px and give up the responsive default, and it's also half of a double-click.
			if (to.x === from.x || nextWidthRef.current == null) {
				return;
			}
			setStored(nextWidthRef.current);
			setCommitting(true);
		},
	});

	// The pointer spends the drag out over page content, far from the few px of grip, so the resize
	// cursor has to come from the body to last the whole gesture. Selection needs no such handling:
	// useDraggable preventDefaults the mousedown, which stops one starting.
	useEffect(() => {
		if (!dragging) {
			return;
		}
		const restore = document.body.style.cursor;
		document.body.style.cursor = "col-resize";
		return () => {
			document.body.style.cursor = restore;
		};
	}, [dragging]);

	const nudgeWidth = useCallback(
		(delta: number) => {
			// Same reason as the drag start: at the default width there is no number to add to yet.
			const from = stored ?? panelRef.current?.getBoundingClientRect().width;
			if (from == null) {
				return;
			}
			setStored(clampChatWidth(from + delta, windowWidth));
			setCommitting(true);
		},
		[panelRef, setStored, stored, windowWidth],
	);

	const resetWidth = useCallback(() => setStored(null), [setStored]);

	return {
		width: stored == null ? null : clampChatWidth(stored, windowWidth),
		resizing: dragging || committing,
		nudgeWidth,
		resetWidth,
	};
}

/** The px a single arrow-key press should move the panel edge, given the key. `0` for keys that aren't ours. */
export function chatWidthKeyStep(key: string, position: SidebarPositions): number {
	// Arrow direction is spatial, not semantic: on the handle of a right-docked panel, ← widens.
	const outward = position === "left" ? "ArrowRight" : "ArrowLeft";
	const inward = position === "left" ? "ArrowLeft" : "ArrowRight";
	if (key === outward) {
		return CHAT_WIDTH_KEY_STEP;
	}
	if (key === inward) {
		return -CHAT_WIDTH_KEY_STEP;
	}
	return 0;
}
